import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, DollarSign, TrendingUp, Clock, ShoppingCart, UserCheck, UserX, Eye, Calendar, Activity, UserPlus, Package, Bell } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    blockedUsers: 0,
    totalCommissionsPaid: 0,
    pendingCommissions: 0,
    totalSales: 0,
    pendingApprovals: 0,
    liveVisitors: 0,
    todayVisitors: 0,
    monthlyVisitors: 0,
    todayOrders: 0,
    todaySignups: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    } else {
      loadStats();
      // Refresh stats every 30 seconds for live data
      const interval = setInterval(loadStats, 30000);
      return () => clearInterval(interval);
    }
  }, [navigate]);

  const loadStats = async () => {
    try {
      const data = await adminAPI.getStats();
      setStats(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load statistics');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Website Traffic Stats - New Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">📊 Website Traffic</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card 
              className="border-3 border-green-300 hover:border-green-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer"
              onClick={() => navigate('/admin/visitor-analytics?view=live')}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Activity className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
                <p className="text-xs text-gray-600 mb-1">Live Visitors</p>
                <p className="text-2xl font-bold text-green-600">{stats.liveVisitors}</p>
                <p className="text-xs text-gray-500 mt-1">Active now</p>
              </CardContent>
            </Card>

            <Card 
              className="border-3 border-blue-300 hover:border-blue-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer"
              onClick={() => navigate('/admin/visitor-analytics?view=today')}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Eye className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-1">Today's Visitors</p>
                <p className="text-2xl font-bold text-blue-600">{stats.todayVisitors}</p>
                <p className="text-xs text-gray-500 mt-1">Unique visits</p>
              </CardContent>
            </Card>

            <Card 
              className="border-3 border-purple-300 hover:border-purple-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer"
              onClick={() => navigate('/admin/visitor-analytics?view=monthly')}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-1">Monthly Visitors</p>
                <p className="text-2xl font-bold text-purple-600">{stats.monthlyVisitors}</p>
                <p className="text-xs text-gray-500 mt-1">This month</p>
              </CardContent>
            </Card>

            <Card 
              className="border-3 border-orange-300 hover:border-orange-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm cursor-pointer"
              onClick={() => navigate('/admin/today-orders')}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Package className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-1">Today's Orders</p>
                <p className="text-2xl font-bold text-orange-600">{stats.todayOrders}</p>
                <p className="text-xs text-gray-500 mt-1">New orders</p>
              </CardContent>
            </Card>

            <Card className="border-3 border-pink-300 hover:border-pink-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="h-5 w-5 text-pink-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-1">Today's Signups</p>
                <p className="text-2xl font-bold text-pink-600">{stats.todaySignups}</p>
                <p className="text-xs text-gray-500 mt-1">New users</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-3 border-red-300 hover:border-red-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
                }}>
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
              <p className="text-xs text-green-600 mt-1">{stats.activeUsers} active</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-yellow-300 hover:border-yellow-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Total Commissions Paid</p>
              <p className="text-3xl font-bold text-green-600">₹{(stats.totalCommissionsPaid || 0).toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-pink-300 hover:border-pink-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Pending Commissions</p>
              <p className="text-3xl font-bold text-orange-600">₹{(stats.pendingCommissions || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.pendingApprovals || 0} pending approvals</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-blue-300 hover:border-blue-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Total Sales</p>
              <p className="text-3xl font-bold text-blue-600">{stats.totalSales || 0}</p>
              <p className="text-xs text-gray-500 mt-1">All time sales</p>
            </CardContent>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-green-600" />
                Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{stats.activeUsers || 0}</p>
              <p className="text-sm text-gray-600 mt-2">Currently active referrers</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserX className="h-5 w-5 text-red-600" />
                Blocked Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-600">{stats.blockedUsers}</p>
              <p className="text-sm text-gray-600 mt-2">Suspended accounts</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-purple-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple-600">₹{(stats.totalCommissionsPaid || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-2">From referral sales</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Button 
            onClick={() => navigate('/admin/send-notification')}
            className="h-auto py-6 text-white border-2 border-blue-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">📢 Send Notification</p>
              <p className="text-sm opacity-90">Broadcast message to all users</p>
            </div>
          </Button>
          
          <Button 
            onClick={() => navigate('/admin/pending-tasks')}
            className="h-auto py-6 text-white border-2 border-orange-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Pending Tasks</p>
              <p className="text-sm opacity-90">View all pending items</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/admin/orders')}
            className="h-auto py-6 text-white border-2 border-green-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Manage Orders</p>
              <p className="text-sm opacity-90">View, accept, update customer orders</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/admin/users')}
            className="h-auto py-6 text-white border-2 border-yellow-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Manage Users</p>
              <p className="text-sm opacity-90">View, edit, block/unblock users</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/admin/visitor-map')}
            className="h-auto py-6 text-white border-2 border-blue-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">🗺️ Visitor Map</p>
              <p className="text-sm opacity-90">View visitors on India map</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/admin/live-visitors')}
            className="h-auto py-6 text-white border-2 border-green-400 shadow-lg animate-pulse"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg flex items-center gap-2">
                📊 Live Visitor Analytics
                <span className="inline-flex items-center gap-1 text-xs bg-white text-green-600 px-2 py-1 rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  LIVE
                </span>
              </p>
              <p className="text-sm opacity-90">Real-time graphs, sources, locations & more</p>
            </div>
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Button 
            onClick={() => navigate('/admin/messages')}
            className="h-auto py-6 text-white border-2 border-pink-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Messages</p>
              <p className="text-sm opacity-90">Reply to user messages and inquiries</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/admin/commissions')}
            className="h-auto py-6 text-white border-2 border-purple-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Manage Commissions</p>
              <p className="text-sm opacity-90">Approve/reject commission payouts</p>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
